import { useEffect, useState } from "react";

interface IpApiResponse {
  city?: string;
  country?: string; // 2-letter ISO code, e.g. "PL"
  bogon?: boolean;
}

// Module-level cache so repeated renders and multiple hook instances share results
const cache = new Map<string, string | null>();
const pending = new Map<string, Promise<string | null>>();

const PRIVATE_IP_RE =
  /^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|::1$|localhost)/i;

async function fetchLocation(ip: string): Promise<string | null> {
  if (PRIVATE_IP_RE.test(ip)) {
    return null;
  }
  try {
    const res = await fetch(`https://ipinfo.io/${ip}/json`);
    if (!res.ok) return null;
    const data: IpApiResponse = await res.json();
    if (data.bogon) return null;
    const parts = [data.city, data.country].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  } catch {
    return null;
  }
}

function getOrFetch(ip: string): Promise<string | null> {
  if (cache.has(ip)) {
    return Promise.resolve(cache.get(ip)!);
  }
  if (pending.has(ip)) {
    return pending.get(ip)!;
  }
  const promise = fetchLocation(ip).then((result) => {
    if (result !== null) {
      cache.set(ip, result);
    }
    pending.delete(ip);
    return result;
  });
  pending.set(ip, promise);
  return promise;
}

/**
 * Returns the resolved city/country label for an IP address, e.g. "Częstochowa, Poland".
 * Returns null while loading or when unavailable.
 */
export function useIpLocation(ip: string | null | undefined): string | null {
  const [location, setLocation] = useState<string | null>(() => {
    if (!ip) return null;
    return cache.has(ip) ? (cache.get(ip) ?? null) : null;
  });

  useEffect(() => {
    let cancelled = false;
    void (ip ? getOrFetch(ip) : Promise.resolve(null)).then((result) => {
      if (!cancelled) setLocation(result);
    });
    return () => {
      cancelled = true;
    };
  }, [ip]);

  return location;
}
